import React, { useState, useEffect } from 'react';
import {
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Shield,
  DollarSign,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader,
  Home,
  Wrench,
  Lock,
} from 'lucide-react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../utils/firebase/client';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import {
  VendorOnboardingData,
  VendorServiceType,
  PaymentTerms,
  PaymentMethod,
} from '../types/vendorInvitation';
import { vendorInvitationService } from '../utils/vendors/invitationService';

interface VendorOnboardingFormProps {
  token: string;
}

const STEPS = [
  { id: 1, title: 'Company Info', icon: Building },
  { id: 2, title: 'Contact & Address', icon: MapPin },
  { id: 3, title: 'Services & Availability', icon: Wrench },
  { id: 4, title: 'Financial & Insurance', icon: Shield },
  { id: 5, title: 'Account Setup', icon: Lock },
  { id: 6, title: 'Review & Submit', icon: CheckCircle },
];

const SERVICE_TYPES: { value: VendorServiceType; label: string }[] = [
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'welding', label: 'Welding' },
  { value: 'equipment_repair', label: 'Equipment Repair' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'painting', label: 'Painting' },
  { value: 'carpentry', label: 'Carpentry' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'general_maintenance', label: 'General Maintenance' },
  { value: 'well_servicing', label: 'Well Servicing' },
  { value: 'pumping_services', label: 'Pumping Services' },
  { value: 'trucking', label: 'Trucking' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_TERMS: { value: PaymentTerms; label: string }[] = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_45', label: 'Net 45' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'net_90', label: 'Net 90' },
];

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH / Direct Deposit' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

export default function VendorOnboardingForm({ token }: VendorOnboardingFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [formData, setFormData] = useState<Partial<VendorOnboardingData>>({
    servicesOffered: [],
    sameAsPhysical: true,
    offers24_7Emergency: false,
    physicalAddress: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
    },
    acceptedTerms: false,
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setIsLoading(true);
      const invitationData = await vendorInvitationService.getInvitationByToken(token);

      if (!invitationData) {
        toast.error('Invalid or expired invitation link');
        return;
      }

      if (invitationData.status === 'completed') {
        toast.info('This invitation has already been completed');
      }

      if (new Date() > invitationData.expiresAt) {
        toast.error('This invitation has expired');
        return;
      }

      setInvitation(invitationData);

      // Pre-fill phone number and company name if available
      setFormData(prev => ({
        ...prev,
        phoneNumber: invitationData.phoneNumber,
        companyName: invitationData.companyNameSuggestion || '',
        contactName: invitationData.contactName || '',
        email: invitationData.email || '',
      }));
    } catch (error) {
      console.error('Error loading invitation:', error);
      toast.error('Failed to load invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field: string, value: string, type: 'physical' | 'mailing' = 'physical') => {
    const addressKey = type === 'physical' ? 'physicalAddress' : 'mailingAddress';
    setFormData(prev => ({
      ...prev,
      [addressKey]: {
        ...(prev[addressKey] || {}),
        [field]: value,
      },
    }));
  };

  const handleServiceToggle = (service: VendorServiceType) => {
    setFormData(prev => {
      const services = prev.servicesOffered || [];
      const isSelected = services.includes(service);

      return {
        ...prev,
        servicesOffered: isSelected
          ? services.filter(s => s !== service)
          : [...services, service],
      };
    });
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.companyName?.trim()) {
          toast.error('Company name is required');
          return false;
        }
        if (!formData.contactName?.trim()) {
          toast.error('Contact name is required');
          return false;
        }
        return true;

      case 2:
        if (!formData.phoneNumber?.trim()) {
          toast.error('Phone number is required');
          return false;
        }
        if (!formData.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Valid email is required');
          return false;
        }
        if (!formData.physicalAddress?.street?.trim()) {
          toast.error('Street address is required');
          return false;
        }
        if (!formData.physicalAddress?.city?.trim()) {
          toast.error('City is required');
          return false;
        }
        if (!formData.physicalAddress?.state?.trim()) {
          toast.error('State is required');
          return false;
        }
        if (!formData.physicalAddress?.zip?.trim()) {
          toast.error('ZIP code is required');
          return false;
        }
        return true;

      case 3:
        if (!formData.servicesOffered?.length) {
          toast.error('Please select at least one service');
          return false;
        }
        return true;

      case 4:
        if (!formData.paymentTerms) {
          toast.error('Please select payment terms');
          return false;
        }
        if (!formData.preferredPaymentMethod) {
          toast.error('Please select preferred payment method');
          return false;
        }
        return true;

      case 5:
        if (!formData.password || formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        return true;

      case 6:
        if (!formData.acceptedTerms) {
          toast.error('You must accept the terms and conditions');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Create Authentication User
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email!,
        formData.password!
      );
      const user = userCredential.user;

      // 2. Update Auth Profile
      await updateProfile(user, {
        displayName: formData.contactName
      });

      // 3. Create User Profile in Firestore (Required for Permissions)
      const userProfile = {
        id: user.uid,
        email: user.email,
        name: formData.contactName,
        firstName: formData.contactName?.split(' ')[0] || '',
        lastName: formData.contactName?.split(' ').slice(1).join(' ') || '',
        role: 'vendor',
        companyId: invitation.companyId, // Link to invited company
        companyName: formData.companyName,
        phone: formData.phoneNumber,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        onboardingStatus: 'completed'
      };

      // Save to companies/{companyId}/users/{uid} (New Structure)
      await setDoc(doc(db, `companies/${invitation.companyId}/users`, user.uid), userProfile);

      // Also save to root users/{uid} for legacy compatibility if needed, 
      // but new rules prioritize the company-scoped path.

      // 4. Submit Vendor Data
      const submissionData: VendorOnboardingData = {
        ...formData as VendorOnboardingData,
        acceptedTermsAt: new Date(),
        acceptedTermsIP: await fetch('https://api.ipify.org?format=json')
          .then(r => r.json())
          .then(d => d.ip)
          .catch(() => 'unknown'),
      };

      // Pass the Auth UID to link the records
      const result = await vendorInvitationService.completeOnboarding(
        token,
        submissionData,
        user.uid
      );

      if (result.success) {
        toast.success(
          <div>
            <p className="font-bold">Profile Submitted!</p>
            <p className="text-sm mt-1">
              {invitation.companyName} will review your information and you'll be notified when approved.
            </p>
          </div>,
          { duration: 7000 }
        );

        // Move to success screen
        setCurrentStep(7);
      } else {
        toast.error(result.message || 'Failed to submit profile');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin h-16 w-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
            <p className="text-gray-600">
              This invitation link is invalid or has expired. Please contact the company that sent you this invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success Screen
  if (currentStep === 7) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-xl w-full shadow-2xl animate-in zoom-in-95 duration-300">
          <CardContent className="pt-10 pb-10 text-center">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Your vendor profile has been submitted to <strong>{invitation.companyName}</strong>.
              You can now access the vendor portal to track your approval status.
            </p>

            <div className="space-y-4">
              <Button
                onClick={() => window.location.href = '/login'}
                className="w-full h-12 text-lg font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
              >
                Log In to Vendor Portal
              </Button>

              <p className="text-xs text-gray-400 mt-4">
                You will receive an SMS notification once your account is fully approved for service tickets.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Vendor Onboarding
            </h1>
            <p className="text-gray-600">
              <span className="font-semibold">{invitation.invitedByName}</span> from{' '}
              <span className="font-semibold text-blue-600">{invitation.companyName}</span>{' '}
              has invited you to become a service vendor
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isCompleted
                        ? 'bg-green-600 text-white'
                        : isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                        }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="h-6 w-6" />
                      ) : (
                        <Icon className="h-6 w-6" />
                      )}
                    </div>
                    <span
                      className={`text-xs mt-2 font-medium text-center ${isActive || isCompleted ? 'text-gray-900' : 'text-gray-500'
                        }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`h-1 flex-1 mx-2 rounded transition-colors ${isCompleted ? 'bg-green-600' : 'bg-gray-200'
                        }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-xl">
            <CardTitle className="text-2xl">
              Step {currentStep}: {STEPS[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Name <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="ABC Plumbing Services"
                      value={formData.companyName || ''}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      className="pl-12 h-14 text-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    DBA Name (if different)
                  </label>
                  <Input
                    placeholder="Doing Business As..."
                    value={formData.dbaName || ''}
                    onChange={(e) => handleChange('dbaName', e.target.value)}
                    className="h-14 text-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Contact Name <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="John Smith"
                      value={formData.contactName || ''}
                      onChange={(e) => handleChange('contactName', e.target.value)}
                      className="pl-12 h-14 text-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Title / Position
                  </label>
                  <Input
                    placeholder="Owner, Manager, etc."
                    value={formData.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="h-14 text-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Website
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="url"
                      placeholder="https://yourcompany.com"
                      value={formData.website || ''}
                      onChange={(e) => handleChange('website', e.target.value)}
                      className="pl-12 h-14 text-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Contact & Address - Continue in next message due to length */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone Number <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phoneNumber || ''}
                        onChange={(e) => handleChange('phoneNumber', e.target.value)}
                        className="pl-12 h-14 text-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Alternate Phone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="tel"
                        placeholder="(555) 987-6543"
                        value={formData.alternatePhone || ''}
                        onChange={(e) => handleChange('alternatePhone', e.target.value)}
                        className="pl-12 h-14 text-lg"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="john@abcplumbing.com"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="pl-12 h-14 text-lg"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Home className="h-5 w-5 mr-2" />
                    Physical Address
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Street Address <span className="text-red-600">*</span>
                      </label>
                      <Input
                        placeholder="123 Main Street"
                        value={formData.physicalAddress?.street || ''}
                        onChange={(e) => handleAddressChange('street', e.target.value)}
                        className="h-12"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Suite / Unit
                      </label>
                      <Input
                        placeholder="Suite 100"
                        value={formData.physicalAddress?.street2 || ''}
                        onChange={(e) => handleAddressChange('street2', e.target.value)}
                        className="h-12"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          City <span className="text-red-600">*</span>
                        </label>
                        <Input
                          placeholder="Houston"
                          value={formData.physicalAddress?.city || ''}
                          onChange={(e) => handleAddressChange('city', e.target.value)}
                          className="h-12"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          State <span className="text-red-600">*</span>
                        </label>
                        <Input
                          placeholder="TX"
                          maxLength={2}
                          value={formData.physicalAddress?.state || ''}
                          onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase())}
                          className="h-12"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          ZIP <span className="text-red-600">*</span>
                        </label>
                        <Input
                          placeholder="77001"
                          maxLength={10}
                          value={formData.physicalAddress?.zip || ''}
                          onChange={(e) => handleAddressChange('zip', e.target.value)}
                          className="h-12"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="sameAsPhysical"
                    checked={formData.sameAsPhysical}
                    onChange={(e) => handleChange('sameAsPhysical', e.target.checked)}
                    className="h-5 w-5 text-blue-600 rounded"
                  />
                  <label htmlFor="sameAsPhysical" className="text-sm font-medium text-gray-700">
                    Mailing address is same as physical address
                  </label>
                </div>
              </div>
            )}

            {/* Step 3: Services & Availability */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Services Offered <span className="text-red-600">*</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICE_TYPES.map((service) => {
                      const isSelected = formData.servicesOffered?.includes(service.value);
                      return (
                        <button
                          key={service.value}
                          type="button"
                          onClick={() => handleServiceToggle(service.value)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${isSelected
                            ? 'border-blue-600 bg-blue-50 text-blue-900'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{service.label}</span>
                            {isSelected && <CheckCircle className="h-5 w-5 text-blue-600" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {formData.servicesOffered?.includes('other') && (
                    <div className="mt-4">
                      <Input
                        placeholder="Describe other services..."
                        value={formData.otherServiceDescription || ''}
                        onChange={(e) => handleChange('otherServiceDescription', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Service Area</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Radius (miles)
                    </label>
                    <Input
                      type="number"
                      placeholder="50"
                      value={formData.serviceAreaRadius || ''}
                      onChange={(e) => handleChange('serviceAreaRadius', parseInt(e.target.value))}
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Availability</h3>

                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      id="emergency"
                      checked={formData.offers24_7Emergency}
                      onChange={(e) => handleChange('offers24_7Emergency', e.target.checked)}
                      className="h-5 w-5 text-blue-600 rounded"
                    />
                    <label htmlFor="emergency" className="text-sm font-medium text-gray-700">
                      Offer 24/7 Emergency Services
                    </label>
                  </div>

                  {formData.offers24_7Emergency && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emergency Phone
                      </label>
                      <Input
                        type="tel"
                        placeholder="(555) 911-9111"
                        value={formData.emergencyPhone || ''}
                        onChange={(e) => handleChange('emergencyPhone', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Response Time SLA
                    </label>
                    <select
                      aria-label="Response Time SLA"
                      value={formData.responseTimeSLA || ''}
                      onChange={(e) => handleChange('responseTimeSLA', e.target.value)}
                      className="w-full h-12 px-4 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select response time...</option>
                      <option value="< 1 hour">&lt; 1 hour</option>
                      <option value="1-4 hours">1-4 hours</option>
                      <option value="Same day">Same day</option>
                      <option value="Next day">Next day</option>
                      <option value="2-3 days">2-3 days</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Financial & Insurance */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Payment Information
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Payment Terms <span className="text-red-600">*</span>
                      </label>
                      <select
                        aria-label="Payment Terms"
                        value={formData.paymentTerms || ''}
                        onChange={(e) => handleChange('paymentTerms', e.target.value)}
                        className="w-full h-12 px-4 border border-gray-300 rounded-lg text-lg"
                      >
                        <option value="">Select payment terms...</option>
                        {PAYMENT_TERMS.map((term) => (
                          <option key={term.value} value={term.value}>
                            {term.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Preferred Payment Method <span className="text-red-600">*</span>
                      </label>
                      <select
                        aria-label="Preferred Payment Method"
                        value={formData.preferredPaymentMethod || ''}
                        onChange={(e) => handleChange('preferredPaymentMethod', e.target.value)}
                        className="w-full h-12 px-4 border border-gray-300 rounded-lg text-lg"
                      >
                        <option value="">Select payment method...</option>
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Tax ID / EIN (Optional)
                      </label>
                      <Input
                        placeholder="12-3456789"
                        value={formData.taxId || ''}
                        onChange={(e) => handleChange('taxId', e.target.value)}
                        className="h-12 text-lg"
                      />
                      <p className="text-xs text-gray-600 mt-1">Required for issuing 1099 forms</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2" />
                    Insurance & Certifications
                  </h3>

                  <div className="bg-blue-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-900">
                      Insurance information is optional now but may be required before receiving certain types of work orders.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="osha30"
                        checked={formData.osha30Certified || false}
                        onChange={(e) => handleChange('osha30Certified', e.target.checked)}
                        className="h-5 w-5 text-blue-600 rounded"
                      />
                      <label htmlFor="osha30" className="text-sm font-medium text-gray-700">
                        OSHA 30 Certified
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Account Setup */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Lock className="h-5 w-5 mr-2" />
                    Create Your Account
                  </h3>
                  <p className="text-gray-600 mb-6 text-sm">
                    Create a password to access the Vendor Portal. You will use your email ({formData.email}) to log in.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Password <span className="text-red-600">*</span>
                      </label>
                      <Input
                        type="password"
                        placeholder="Min. 8 characters"
                        value={formData.password || ''}
                        onChange={(e) => handleChange('password', e.target.value)}
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Confirm Password <span className="text-red-600">*</span>
                      </label>
                      <Input
                        type="password"
                        placeholder="Re-enter password"
                        value={formData.confirmPassword || ''}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Review & Submit */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-4">Review Your Information</h3>

                  <div className="space-y-4 text-sm">
                    <div className="bg-white rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-2">Company</p>
                      <p className="text-gray-700">{formData.companyName}</p>
                      <p className="text-gray-600">{formData.contactName}</p>
                    </div>

                    <div className="bg-white rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-2">Contact</p>
                      <p className="text-gray-700">{formData.phoneNumber}</p>
                      <p className="text-gray-600">{formData.email}</p>
                    </div>

                    <div className="bg-white rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-2">Services</p>
                      <p className="text-gray-700">
                        {formData.servicesOffered?.map(s =>
                          SERVICE_TYPES.find(st => st.value === s)?.label
                        ).join(', ')}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-2">Payment</p>
                      <p className="text-gray-700">
                        {PAYMENT_TERMS.find(t => t.value === formData.paymentTerms)?.label} via{' '}
                        {PAYMENT_METHODS.find(m => m.value === formData.preferredPaymentMethod)?.label}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={formData.acceptedTerms}
                      onChange={(e) => handleChange('acceptedTerms', e.target.checked)}
                      className="h-5 w-5 text-blue-600 rounded mt-0.5"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-700">
                      <span className="font-semibold">I accept the terms and conditions</span>
                      <br />
                      I confirm that the information provided is accurate and I agree to {invitation.companyName}'s vendor
                      terms and conditions. I understand that this information may be verified and that providing false
                      information may result in removal from the vendor list.
                    </label>
                  </div>
                </div>
              </div>
            )}
          </CardContent>

          {/* Navigation */}
          <div className="bg-gray-50 px-8 py-6 rounded-b-xl flex items-center justify-between border-t">
            <Button
              onClick={handlePrevious}
              variant="outline"
              disabled={currentStep === 1 || isSubmitting}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Previous</span>
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-8"
              >
                <span>Next</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.acceptedTerms}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-8"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span>Submit Profile</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-600">
          <p>Powered by OilWellX • Secure vendor onboarding</p>
        </div>
      </div>
    </div>
  );
}
